import { useEffect, useState } from "react";
import { Link } from "wouter";
import { getConsent, setConsent } from "@/lib/consent";
import { trackPageView } from "@/lib/analytics-tracker";
import { useLocale } from "@/lib/locale";

// ─── Banner consenso cookie / privacy ────────────────────────────────────────
// Mostrato finché l'utente non sceglie. "Accetta" abilita le statistiche
// interne (anonime, prima parte); "Rifiuta" le disattiva e rimuove gli ID.

export function CookieConsent() {
  const [visible, setVisible] = useState(false);
  const { locale, localePath } = useLocale();
  const copy = {
    it: { aria: "Consenso cookie", title: "La tua privacy", body: "Questo sito utilizza solo cookie tecnici e, previo tuo consenso, statistiche anonime di prima parte per capire come viene usato il sito. Nessun dato viene condiviso con terze parti. Maggiori informazioni nella", privacy: "Privacy Policy", and: "e nella", cookies: "Cookie Policy", accept: "Accetta", reject: "Rifiuta" },
    en: { aria: "Cookie consent", title: "Your privacy", body: "This site uses only technical cookies and, with your consent, anonymous first-party statistics to understand how the site is used. No data is shared with third parties. More information in the", privacy: "Privacy Policy", and: "and the", cookies: "Cookie Policy", accept: "Accept", reject: "Reject" },
    de: { aria: "Cookie-Einwilligung", title: "Ihre Privatsphäre", body: "Diese Website verwendet nur technische Cookies und mit Ihrer Zustimmung anonyme Statistiken aus erster Hand, um die Nutzung der Website zu verstehen. Es werden keine Daten an Dritte weitergegeben. Weitere Informationen in der", privacy: "Datenschutzerklärung", and: "und der", cookies: "Cookie-Richtlinie", accept: "Akzeptieren", reject: "Ablehnen" },
    hr: { aria: "Privola za kolačiće", title: "Vaša privatnost", body: "Ova stranica koristi samo tehničke kolačiće i, uz vašu privolu, anonimnu statistiku prve strane za razumijevanje korištenja stranice. Podaci se ne dijele s trećim stranama. Više informacija u", privacy: "Pravilima privatnosti", and: "i", cookies: "Pravilima o kolačićima", accept: "Prihvati", reject: "Odbij" },
  }[locale];

  useEffect(() => {
    if (getConsent() === null) setVisible(true);
  }, []);

  if (!visible) return null;

  const choose = (value: "accepted" | "rejected") => {
    setConsent(value);
    setVisible(false);
    // Con il consenso appena dato, registra subito la visita corrente
    if (value === "accepted") trackPageView();
  };

  return (
    <div
      role="dialog"
      aria-label={copy.aria}
      data-testid="dialog-cookie-consent"
      className="fixed inset-x-0 bottom-[calc(max(0.5rem,env(safe-area-inset-bottom))+3.25rem)] z-[70] p-3 sm:bottom-0 sm:p-6"
    >
      <div className="mx-auto max-w-3xl rounded-2xl border border-border bg-background shadow-2xl shadow-black/20 p-5 sm:p-6">
        <h2 className="font-bold text-foreground mb-2">{copy.title}</h2>
        <p className="text-sm text-muted-foreground leading-relaxed mb-4">
          {copy.body}{" "}
          <Link href={localePath("/privacy")} className="underline text-primary">
            {copy.privacy}
          </Link>{" "}
          {copy.and}{" "}
          <Link href={localePath("/cookie")} className="underline text-primary">
            {copy.cookies}
          </Link>
          .
        </p>
        <div className="flex flex-col sm:flex-row gap-3">
          <button
            onClick={() => choose("accepted")}
            data-testid="button-accept-cookies"
            className="flex-1 rounded-full bg-primary text-primary-foreground font-semibold py-2.5 px-6 hover:opacity-90 transition-opacity"
          >
            {copy.accept}
          </button>
          <button
            onClick={() => choose("rejected")}
            data-testid="button-reject-cookies"
            className="flex-1 rounded-full border border-border text-foreground font-semibold py-2.5 px-6 hover:bg-muted transition-colors"
          >
            {copy.reject}
          </button>
        </div>
      </div>
    </div>
  );
}
